import {
	MethodologySumModel,
	ComponentObjectivePDFModel,
	ManageOrderResponseModel,
	ContentExposureReaderRequestModel,
	PreviewItemRequestModel,
	ExposurePreviewContentModel
} from "./model";
import {
	GLServiceBase,
	ServiceRoute,
	GLUtil,
	getServiceMethodKey,
	HttpMethod,
	ServerExceptionCode,
	fmtMsg
} from "commonui";
import { IContentExposureService } from ".";
import { ContentExposureLocale, MethodologyLocale } from "@/locales/localeid";
import { MethodologyType } from "@/util/enum";
import { SignatureResoureModel } from "../admin";

function convertMethodologies({ error_description }: { error_description: string[] }) {
	return error_description[0]
		.split(":")
		.pop()
		?.split(",")
		.map((d) => {
			return fmtMsg(
				{
					[MethodologyType.Classic]: MethodologyLocale.Classic,
					[MethodologyType.Remote]: MethodologyLocale.Remote,
					[MethodologyType.Digital]: MethodologyLocale.Digital,
					[MethodologyType.Independent]: MethodologyLocale.Independent
				}[d]
			);
		})
		.join(", ");
}
function convertLessonPlanVersions({ error_description }: { error_description: string[] }) {
	return (error_description[0].match(/\((.*)\)/) as string[])[1];
}
class ContentExposureServiceClass extends GLServiceBase<any, any> implements IContentExposureService {
	serviceRoute: ServiceRoute = { prefix: "content/v1/contentexposures" };
	duplicatingSuffix = "duplicating/{targetLessonPlanVersionId}";
	errMsgs = (() => {
		const result = {};
		result[getServiceMethodKey(this, HttpMethod.Post, ServerExceptionCode.TargetHasExistedException)] = {
			id: ContentExposureLocale.ContentExposureWarningMethodologyUpperLimit,
			values: { names: convertMethodologies }
		};
		result[
			getServiceMethodKey(
				this,
				HttpMethod.Post,
				ServerExceptionCode.TargetHasExistedException,
				`${this.serviceRoute.prefix}/${this.duplicatingSuffix}`
			)
		] = {
			id: ContentExposureLocale.ContentExposureWarningMethodologyUpperLimit,
			values: { names: convertMethodologies }
		};
		result[getServiceMethodKey(this, HttpMethod.Put, ServerExceptionCode.TargetHasExistedException)] = {
			id: ContentExposureLocale.ContentExposureWarningAttachLessonPlanSlot,
			values: { names: convertLessonPlanVersions }
		};
		result[getServiceMethodKey(this, HttpMethod.Delete, ServerExceptionCode.TargetHasExistedException)] = {
			id: ContentExposureLocale.ContentExposureWarningAttachLessonPlan,
			values: { names: convertLessonPlanVersions }
		};
		return result;
	})();
	getContentExposures(params: any): Promise<any[]> {
		return this.get(params);
	}
	getDefaultMediaSlot(params: any): Promise<any> {
		return this.get("defaultmediaslot", params);
	}
	attachContentExposures(params: any): Promise<void> {
		return this.update("batching", params);
	}
	duplicateContentExposure(contentExposureIds: any[], targetLessonPlanVersionId: string): Promise<any> {
		return this.create(this.duplicatingSuffix, contentExposureIds, {
			targetLessonPlanVersionId: targetLessonPlanVersionId
		});
	}
	deleteContentExposure(params: any): Promise<void> {
		return this.delete("{id}", null, params);
	}
	exportComponentObjectivesPDF(params: ComponentObjectivePDFModel): Promise<any> {
		return this.getBlob("componentobjectives/pdf", params);
	}
	exportComponentObjectivesPDFZip(params: ComponentObjectivePDFModel): Promise<any> {
		return this.getBlob("componentobjectives/zip", params);
	}
	getTeachingActivitySas(): Promise<SignatureResoureModel> {
		return this.get("teachingactivitysas");
	}

	getManageOrderItemsForExposure(
		parentContentId: string,
		contentExposureId: string
	): Promise<ManageOrderResponseModel> {
		return this.get("exposureItems", { parentContentId, contentExposureId });
	}

	saveExposureItems(exposureId: string, params: ContentExposureReaderRequestModel[]) {
		return this.create("{exposureId}/saveItemsOrder", params, { exposureId });
	}

	previewItems(exposureId: string, requestModel: PreviewItemRequestModel): Promise<ExposurePreviewContentModel[]> {
		return this.create("{exposureId}/previewItems", requestModel, { exposureId });
	}

	applyOnAllExposures(
		parentContentId: string,
		lpvId: string,
		exposureId: string,
		exposureSelectedItems: ContentExposureReaderRequestModel[]
	): Promise<void> {
		return this.create("{parentContentId}/{lpvId}/{exposureId}/orderAllExposuresItems", exposureSelectedItems, {
			parentContentId,
			lpvId,
			exposureId
		});
	}

	isChapterInContentExposureReaderContent(exposureId: string, contentId: string): Promise<boolean> {
		return this.get("{exposureId}/{chapterId}/isInContentExposureReaderContent", {
			chapterId: contentId,
			exposureId
		});
	}
}

export const ContentExposureService = new ContentExposureServiceClass();
